<template>
	<div :class="classes">
		<slot></slot>
	</div>
</template>

<script>
import {
	TeamingButtonSize,
	TeamingButtonType
} from "@teaming/common-enums";

/**
 * Enforces button layout in terms of color, size, padding, and margins.
 * Designed to be extended for specialized cases.
 * @see TeamingButton2.vue
 */
export default {
	name: "ButtonLayout",
	props: {
		size: {
			type: String,
			default: TeamingButtonSize.MEDIUM,
			validator: value => TeamingButtonSize.includes( value ),
		},
		type: {
			type: String,
			default: TeamingButtonType.PRIMARY,
			validator: value => TeamingButtonType.includes( value ),
		},
		//
		// states
		//
		disabled: {
			type: Boolean,
			default: false,
		},
		active: {
			type: Boolean,
			default: false,
		},
		focused: {
			type: Boolean,
			default: false,
		},
		//
		// variants
		//
		borderless: {
			type: Boolean,
			default: false,
		},
		destructive: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		classes() {
			return {
				"button-layout": true,
				"size--md": ( this.size === TeamingButtonSize.MEDIUM ),
				"size--sm": ( this.size === TeamingButtonSize.SMALL ),
				"size--xs": ( this.size === TeamingButtonSize.TINY ),
				"type--primary": ( this.type === TeamingButtonType.PRIMARY ),
				"type--secondary": ( this.type === TeamingButtonType.SECONDARY ),
				"type--tertiary": ( this.type === TeamingButtonType.TERTIARY ),
				"type--ghost": ( this.type === TeamingButtonType.QUATERNARY ),
				"state--disabled": this.disabled,
				"state--focused": this.focused, // TODO: how to handle focus state?
				"state--active": this.active,
				// "state--hover": false,
				"state--enabled": false,
				"variant--borderless": this.borderless,
				"variant--destructive": this.destructive,
			};
		},
	},
};
</script>

<style lang="scss" scoped>
.button-layout {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;

	border: 0.125rem solid transparent;
	border-radius: 0.5rem;
	cursor: pointer;
	padding: 0.5rem 0.65rem;
}

.button-layout.state--disabled {
	cursor: not-allowed;
}

.button-layout.variant--borderless {
	border: none;
}

.button-layout.size--md {
	padding: .5rem .75rem .5rem .75rem;
	min-width: 2.5rem;
	@include font-and-line-for(1);
}

.button-layout.size--sm {
	padding: .375rem .5rem .375rem .5rem;
	min-width: 2rem;
	height: 2rem;
	@include font-and-line-for(0);
}

.button-layout.size--xs {
	padding: 0.25rem 0.375rem 0.25rem 0.375rem;
	min-width: 1.5rem;
	height: 1.5rem;
	@include font-and-line-for(-1);
}

//
// PRIMARY
//

$typePrimaryColor: $white;
$typePrimaryBGColor: $color2-500;
$typePrimaryBorderColor: $color2-500;
$typePrimaryColor--stateHover: $white;
$typePrimaryBGColor--stateHover: $color2-600;
$typePrimaryBorderColor--stateHover: $color2-600;
$typePrimaryColor--stateFocused: $white;
$typePrimaryBGColor--stateFocused: $color2-500;
$typePrimaryBorderColor--stateFocused: $color2-700;
$typePrimaryColor--stateDisabled: $white;
$typePrimaryBGColor--stateDisabled: $color1-100;
$typePrimaryBorderColor--stateDisabled: $color1-100;
.button-layout.type--primary:not(.variant--destructive) {
	color: $typePrimaryColor;
	background-color: $typePrimaryBGColor;
	border-color: $typePrimaryBorderColor;

	&:hover {
		color: $typePrimaryColor--stateHover;
		background-color: $typePrimaryBGColor--stateHover;
		border-color: $typePrimaryBorderColor--stateHover;
	}

	&.state--focused {
		color: $typePrimaryColor--stateFocused;
		background-color: $typePrimaryBGColor--stateFocused;
		border-color: $typePrimaryBorderColor--stateFocused;
	}

	&.state--disabled {
		color: $typePrimaryColor--stateDisabled;
		background-color: $typePrimaryBGColor--stateDisabled;
		border-color: $typePrimaryBorderColor--stateDisabled;
	}
}

$typePrimaryColor--variantDestructive: $white;
$typePrimaryBGColor--variantDestructive: $color7-500;
$typePrimaryBorderColor--variantDestructive: $color7-500;
$typePrimaryColor--stateHovered--variantDestructive: $white;
$typePrimaryBGColor--stateHovered--variantDestructive: $color7-600;
$typePrimaryBorderColor--stateHovered--variantDestructive: $color7-600;
$typePrimaryColor--stateFocused--variantDestructive: $white;
$typePrimaryBGColor--stateFocused--variantDestructive: $color7-500;
$typePrimaryBorderColor--stateFocused--variantDestructive: $color7-700;
$typePrimaryColor--stateDisabled--variantDestructive: $white;
$typePrimaryBGColor--stateDisabled--variantDestructive: $color1-100;
$typePrimaryBorderColor--stateDisabled--variantDestructive: $color1-100;
.button-layout.type--primary.variant--destructive {
	color: $typePrimaryColor--variantDestructive;
	background-color: $typePrimaryBGColor--variantDestructive;
	border-color: $typePrimaryBorderColor--variantDestructive;

	&:hover {
		color: $typePrimaryColor--stateHovered--variantDestructive;
		background-color: $typePrimaryBGColor--stateHovered--variantDestructive;
		border-color: $typePrimaryBorderColor--stateHovered--variantDestructive;
	}

	&.state--focused {
		color: $typePrimaryColor--stateFocused--variantDestructive;
		background-color: $typePrimaryBGColor--stateFocused--variantDestructive;
		border-color: $typePrimaryBorderColor--stateFocused--variantDestructive;
	}

	&.state--disabled {
		color: $typePrimaryColor--stateDisabled--variantDestructive;
		background-color: $typePrimaryBGColor--stateDisabled--variantDestructive;
		border-color: $typePrimaryBorderColor--stateDisabled--variantDestructive;
	}
}

//
// SECONDARY
//

$typeSecondaryColor: $color2-500;
$typeSecondaryBGColor: $color2-050;
$typeSecondaryBorderColor: $color2-100;
$typeSecondaryColor--stateHover: $color2-600;
$typeSecondaryBGColor--stateHover: $color2-100;
$typeSecondaryBorderColor--stateHover: $color2-200;
$typeSecondaryColor--stateFocused: $color2-700;
$typeSecondaryBGColor--stateFocused: $color2-050;
$typeSecondaryBorderColor--stateFocused: $color2-700;
$typeSecondaryColor--stateDisabled: $color1-100; // ?
$typeSecondaryBGColor--stateDisabled: $white; // ?
$typeSecondaryBorderColor--stateDisabled: $color1-100; // ?
.button-layout.type--secondary:not(.variant--destructive) {
	color: $typeSecondaryColor;
	background-color: $typeSecondaryBGColor;
	border-color: $typeSecondaryBorderColor;

	&:hover {
		color: $typeSecondaryColor--stateHover;
		background-color: $typeSecondaryBGColor--stateHover;
		border-color: $typeSecondaryBorderColor--stateHover;
	}

	&.state--focused {
		color: $typeSecondaryColor--stateFocused;
		background-color: $typeSecondaryBGColor--stateFocused;
		border-color: $typeSecondaryBorderColor--stateFocused;
	}

	&.state--disabled {
		color: $typeSecondaryColor--stateDisabled;
		background-color: $typeSecondaryBGColor--stateDisabled;
		border-color: $typeSecondaryBorderColor--stateDisabled;
	}
}

$typeSecondaryColor--variantDestructive: $color7-500;
$typeSecondaryBGColor--variantDestructive: $color7-050;
$typeSecondaryBorderColor--variantDestructive: $color7-100;
$typeSecondaryColor--stateHovered--variantDestructive: $color7-600;
$typeSecondaryBGColor--stateHovered--variantDestructive: $color7-100;
$typeSecondaryBorderColor--stateHovered--variantDestructive: $color7-200;
$typeSecondaryColor--stateFocused--variantDestructive: $color7-700;
$typeSecondaryBGColor--stateFocused--variantDestructive: $color7-050;
$typeSecondaryBorderColor--stateFocused--variantDestructive: $color7-700;
$typeSecondaryColor--stateDisabled--variantDestructive: $color1-100; // ?
$typeSecondaryBGColor--stateDisabled--variantDestructive: $white; // ?
$typeSecondaryBorderColor--stateDisabled--variantDestructive: $color1-100; // ?
.button-layout.type--secondary.variant--destructive {
	color: $typeSecondaryColor--variantDestructive;
	background-color: $typeSecondaryBGColor--variantDestructive;
	border-color: $typeSecondaryBorderColor--variantDestructive;

	&:hover {
		color: $typeSecondaryColor--stateHovered--variantDestructive;
		background-color: $typeSecondaryBGColor--stateHovered--variantDestructive;
		border-color: $typeSecondaryBorderColor--stateHovered--variantDestructive;
	}

	&.state--focused {
		color: $typeSecondaryColor--stateFocused--variantDestructive;
		background-color: $typeSecondaryBGColor--stateFocused--variantDestructive;
		border-color: $typeSecondaryBorderColor--stateFocused--variantDestructive;
	}

	&.state--disabled {
		color: $typeSecondaryColor--stateDisabled--variantDestructive;
		background-color: $typeSecondaryBGColor--stateDisabled--variantDestructive;
		border-color: $typeSecondaryBorderColor--stateDisabled--variantDestructive;
	}
}

//
// TERTIARY
//

$typeTertiaryColor: $color1-500;
$typeTertiaryBGColor: $color1-050;
$typeTertiaryBorderColor: $color1-100;
$typeTertiaryColor--stateHover: $color1-600;
$typeTertiaryBGColor--stateHover: $color1-100;
$typeTertiaryBorderColor--stateHover: $color1-200;
$typeTertiaryColor--stateFocused: $color1-700;
$typeTertiaryBGColor--stateFocused: $color1-050;
$typeTertiaryBorderColor--stateFocused: $color2-700;
$typeTertiaryColor--stateDisabled: $color1-100; // ?
$typeTertiaryBGColor--stateDisabled: $white; // ?
$typeTertiaryBorderColor--stateDisabled: $color1-100; // ?
.button-layout.type--tertiary { // does this have a destructive variant?
	color: $typeTertiaryColor;
	background-color: $typeTertiaryBGColor;
	border-color: $typeTertiaryBorderColor;

	&:hover {
		color: $typeTertiaryColor--stateHover;
		background-color: $typeTertiaryBGColor--stateHover;
		border-color: $typeTertiaryBorderColor--stateHover;
	}

	&.state--focused {
		color: $typeTertiaryColor--stateFocused;
		background-color: $typeTertiaryBGColor--stateFocused;
		border-color: $typeTertiaryBorderColor--stateFocused;
	}

	&.state--disabled {
		color: $typeTertiaryColor--stateDisabled;
		background-color: $typeTertiaryBGColor--stateDisabled;
		border-color: $typeTertiaryBorderColor--stateDisabled;
	}
}

//
// QUATERNARY / GHOST
//

$typeGhostColor: $color1-500;
$typeGhostBGColor: $white;
$typeGhostBorderColor: $white;
$typeGhostColor--stateHover: $color1-600;
$typeGhostBGColor--stateHover: $white;
$typeGhostBorderColor--stateHover: $color1-050;
$typeGhostColor--stateFocused: $color1-600;
$typeGhostBGColor--stateFocused: $white;
$typeGhostBorderColor--stateFocused: $color2-700;
$typeGhostColor--stateDisabled: $color1-100; // ?
$typeGhostBGColor--stateDisabled: $white; // ?
$typeGhostBorderColor--stateDisabled: $color1-100; // ?
$typeGhostColor--stateActive: $white;
$typeGhostBGColor--stateActive: $color1-500;
$typeGhostBorderColor--stateActive: $color1-500;
.button-layout.type--ghost:not(.variant--destructive) {
	color: $typeGhostColor;
	background-color: $typeGhostBGColor;
	border-color: $typeGhostBorderColor;

	&:hover {
		color: $typeGhostColor--stateHover;
		background-color: $typeGhostBGColor--stateHover;
		border-color: $typeGhostBorderColor--stateHover;
	}

	&.state--focused {
		color: $typeGhostColor--stateFocused;
		background-color: $typeGhostBGColor--stateFocused;
		border-color: $typeGhostBorderColor--stateFocused;
	}

	&.state--disabled {
		color: $typeGhostColor--stateDisabled;
		background-color: $typeGhostBGColor--stateDisabled;
		border-color: $typeGhostBorderColor--stateDisabled;
	}

	&.state--active {
		color: $typeGhostColor--stateActive;
		background-color: $typeGhostBGColor--stateActive;
		border-color: $typeGhostBorderColor--stateActive;
	}
}

$typeGhostColor--variantDestructive: $color7-500;
$typeGhostBGColor--variantDestructive: $color7-050;
$typeGhostBorderColor--variantDestructive: $color7-100;
$typeGhostColor--stateHovered--variantDestructive: $color7-600;
$typeGhostBGColor--stateHovered--variantDestructive: $color7-100;
$typeGhostBorderColor--stateHovered--variantDestructive: $color7-200;
$typeGhostColor--stateFocused--variantDestructive: $color7-700;
$typeGhostBGColor--stateFocused--variantDestructive: $color7-050;
$typeGhostBorderColor--stateFocused--variantDestructive: $color7-700;
$typeGhostColor--stateDisabled--variantDestructive: $color7-100; // ?
$typeGhostBGColor--stateDisabled--variantDestructive: $white; // ?
$typeGhostBorderColor--stateDisabled--variantDestructive: $color7-100; // ?
.button-layout.type--ghost.variant--destructive {
	color: $typeGhostColor--variantDestructive;
	background-color: $typeGhostBGColor--variantDestructive;
	border-color: $typeGhostBorderColor--variantDestructive;

	&:hover {
		color: $typeGhostColor--stateHovered--variantDestructive;
		background-color: $typeGhostBGColor--stateHovered--variantDestructive;
		border-color: $typeGhostBorderColor--stateHovered--variantDestructive;
	}

	&.state--focused {
		color: $typeGhostColor--stateFocused--variantDestructive;
		background-color: $typeGhostBGColor--stateFocused--variantDestructive;
		border-color: $typeGhostBorderColor--stateFocused--variantDestructive;
	}

	&.state--disabled {
		color: $typeGhostColor--stateDisabled--variantDestructive;
		background-color: $typeGhostBGColor--stateDisabled--variantDestructive;
		border-color: $typeGhostBorderColor--stateDisabled--variantDestructive;
	}
}
</style>
